/* eslint-disable react/prop-types */
import moment from 'moment';
import React from 'react';
import { useTheme } from 'styled-components';

// Gen-3 date chip: an orange vertical label on the left and a panel with a big
// value + small sub-label on the right. The orange sidebar is brand-fixed; the
// panel follows the theme (dark navy on dark, light surface on light) and can be
// forced with `variant="light"|"dark"`. Pass sidebar/main/sub directly, or build
// them from an event with `eventDateCardProps`.

const ORANGE = '#fa4616';

const PANELS = {
  dark: {
    bg: '#101026',
    main: '#ffffff',
    sub: 'rgba(255, 255, 255, 0.62)',
    border: 'none',
    shadow: '0 8px 24px rgba(23, 37, 69, 0.18)',
  },
  light: {
    bg: '#ffffff',
    main: '#1f2a30',
    sub: 'rgba(31, 42, 48, 0.5)',
    border: '1px solid rgba(31, 42, 48, 0.1)',
    shadow: '0 8px 24px rgba(23, 37, 69, 0.12)',
  },
};

const DateCard = ({ sidebar, main, sub, height = 64, variant }) => {
  const theme = useTheme();
  const mode = variant || (theme && theme.name === 'light' ? 'light' : 'dark');
  const p = PANELS[mode] || PANELS.dark;
  // Multi-day mains carry a range (dash or space) and read wider; single-day
  // mains are short, so tighten the panel to keep the chip squareish.
  const wide = /[–\-\s]/.test(String(main || '').trim());
  return (
    <div
      style={{
        display: 'inline-flex',
        height,
        borderRadius: 11,
        overflow: 'hidden',
        flexShrink: 0,
        border: p.border,
        boxShadow: p.shadow,
      }}
    >
      {sidebar ? (
        <div
          style={{
            background: ORANGE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 7px',
          }}
        >
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
            }}
          >
            {sidebar}
          </span>
        </div>
      ) : null}
      <div
        style={{
          background: p.bg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: wide ? '0 14px' : '0 10px',
          minWidth: wide ? 60 : 42,
        }}
      >
        <span
          style={{
            color: p.main,
            fontSize: 26,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.5px',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {main}
        </span>
        {sub ? (
          <span
            style={{
              color: p.sub,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              marginTop: 4,
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const parse = (d) => moment(d, 'MM-DD-YYYY');

// Derive DateCard props from an event (start_date/end_date in MM-DD-YYYY).
// Single day -> weekday sidebar, day number, month sub. Multi day -> month
// sidebar, day range, weekday-range sub.
export const eventDateCardProps = (event) => {
  if (!event) return null;
  const start = parse(event.start_date);
  if (!start.isValid()) {
    return {
      sidebar: event.month || '',
      main: event.day || event.date || '',
      sub: '',
    };
  }
  if (event.isMultiDay && event.end_date) {
    const end = parse(event.end_date);
    const sameMonth =
      end.isValid() &&
      start.month() === end.month() &&
      start.year() === end.year();
    return {
      sidebar: start.format('MMM').toUpperCase(),
      main:
        end.isValid() && sameMonth
          ? `${start.format('D')}–${end.format('D')}`
          : end.isValid()
          ? `${start.format('D')} – ${end.format('MMM D')}`
          : start.format('D'),
      sub: end.isValid()
        ? `${start.format('ddd')}–${end.format('ddd')}`.toUpperCase()
        : start.format('ddd').toUpperCase(),
    };
  }
  return {
    sidebar: start.format('ddd').toUpperCase(),
    main: start.format('D'),
    sub: start.format('MMM').toUpperCase(),
  };
};

export default DateCard;
